import { Component, Input, OnInit } from '@angular/core';
import { ConfigurationService } from '../services/configuration.service';
import { PriceService } from '../services/price.service';
import { LanguageService } from '../services/language.service';
import { environment } from '../../environments/environment';
import { NotificationService } from '../services/notification.service';
import { PriceInformation } from '../models/price-information';
import { ThreeDService } from '../services/three-d.service';
import { TranslateService } from '@ngx-translate/core';
import { DoorPosition } from '../models/doorPosition.model';
import { DoorPrice } from '../models/doorPrice.model';
import { DoorType } from '../enums/doorType.enum';
import { DoorDirection } from '../enums/doorDirection.enum';
import { FinishTypes } from '../enums/finishTypes.enum';
import { CompletionService } from '../services/completion.service';
import { Configuration } from '../models/configuration.model';
import { Purpose } from '../enums/purpose';
import { InnerWall } from '../models/innerWall.model';
import { ModularGoogleAnalyticsService } from '../services/modular-google-analytics.service';
import { Conflicts } from '../models/conflicts.model';

@Component({
  selector: 'app-summary-price-list',
  templateUrl: './summary-price-list.component.html',
  styleUrls: ['./summary-price-list.component.scss'],
})
export class SummaryPriceListComponent implements OnInit {
  @Input() loading = false;
  _totalPrice: number;
  imageSrc: any;
  modularPrice: number | false;
  floorPrice: number | boolean;
  ventilationProfilesPrice: number | boolean;
  plinthsPrice: number | boolean;
  ceilingMoldingsPrice: number | boolean;
  doors: DoorPosition[];
  ready = false;
  wallFinishPrices: Record<FinishTypes, number | false>;
  addedToCart = false;
  airPressureEqualizerValvePrice: number | boolean;
  airPressureEqualizerValveCount: number | boolean;
  uProfilesPrice: number | boolean;
  imageSrcInside: string;
  conflicts: Conflicts;

  constructor(
    private configurationService: ConfigurationService,
    private priceService: PriceService,
    private languageService: LanguageService,
    private notificationService: NotificationService,
    private threeDService: ThreeDService,
    private translateService: TranslateService,
    private completionService: CompletionService,
    private modularGoogleAnalyticsService: ModularGoogleAnalyticsService
  ) {
    this.doors = this.configurationService.configuration.settings.doorSettings;
    if (this.doors.length > 0) {
      this.findPricesForEachDoor().then(() => (this.ready = true));
    } else {
      this.ready = true;
    }
  }

  get hasCollisions(): boolean {
    if (!this.conflicts) {
      return false;
    }
    return this.conflicts.collisions.length > 0 || this.conflicts.invalidPanels.length > 0;
  }

  async ngOnInit() {
    this.priceService.priceInformation$.subscribe((priceInformation: PriceInformation) => {
      this._totalPrice = priceInformation.price;
    });
    this.conflicts = this.threeDService.getConflicts();
    this.imageSrc = this.threeDService.getMainScreenshot();
    this.imageSrcInside = this.threeDService.getMainScreenshot(false, true);
    this.modularPrice = this.priceService.calculateModularBasicPrice(this.configurationService.configuration);
    this.floorPrice = this.priceService.calculateAdditionalCostsOfFloor(this.configurationService.configuration);
    this.ventilationProfilesPrice = this.priceService.calculateCostOfVentilationProfiles(
      this.configurationService.configuration
    );
    this.plinthsPrice = this.priceService.calculateCostOfPlinths(this.configurationService.configuration);
    this.ceilingMoldingsPrice = 0;
    this.airPressureEqualizerValvePrice = this.priceService.calculateCostOfAirPressureEqualizerValve(
      this.configurationService.configuration
    );
    this.airPressureEqualizerValveCount = this.priceService.calculateNumberOfAirPressureEqualizerValves(
      this.configurationService.configuration
    );
    this.getPricesForWallFinishes();
    this.uProfilesPrice = this.priceService.calculatePriceOfUProfiles(this.configurationService.configuration);
    this.configurationService.shoppingCartIdReady$.subscribe((configuration: Configuration) => {
      this.completionService.complete(configuration.configurationId, configuration.settings.itemId);
    });
  }

  async findPricesForEachDoor() {
    this.doors = this.doors.sort((a, b) => {
      if (a.side > b.side) {
        return 1;
      }
      if (a.side < b.side) {
        return -1;
      }
      if (a.left > b.left) {
        return 1;
      }
      if (a.left < b.left) {
        return -1;
      }
      return 0;
    });
    // tslint:disable-next-line: forin
    for (const door of this.doors) {
      const priceForDoor = await this.getDoorPrice(door);
      if (priceForDoor === false) {
        return;
      }
      door.salesPrice = priceForDoor.Price;
    }
    const price = await this.getDoorPrice(this.doors[0]);
    if (price === false) {
      return;
    }
    this.doors[0].salesPrice = price.Price;
  }

  getDoorPrice(door: DoorPosition): Promise<false | DoorPrice> {
    return this.priceService.getPriceOfDoor(
      door,
      this.configurationService.configuration.settings.wallThickness,
      this.configurationService.purpose !== Purpose.Cooling
    );
  }

  getDoorText(door: DoorPosition): string {
    let text = this.doorTypeToString(door.doorModel.type);
    text += ' ';
    text += this.translateService.instant('WALL.' + door.side);
    return text;
  }

  doorTypeToString(doorType: DoorType) {
    switch (doorType) {
      case DoorType.Slide:
        return this.translateService.instant('STEP5.SLIDE');
      case DoorType.Turn:
        return this.translateService.instant('STEP5.TURN');
    }
  }

  getDoorImage(door: DoorPosition): string {
    switch (door.doorModel.type) {
      case DoorType.Turn:
        if (door.doorModel.direction === DoorDirection.left) {
          return 'assets/images/doors/turning_left.png';
        }
        return 'assets/images/doors/turning.png';
      case DoorType.Slide:
        if (door.doorModel.direction === DoorDirection.left) {
          return 'assets/images/doors/sliding_left.png';
        }
        return 'assets/images/doors/sliding.png';
    }
  }

  get innerWalls(): InnerWall[] {
    return this.configurationService.configuration.settings.innerWalls;
  }

  get totalPrice(): number {
    return this._totalPrice;
  }

  get floorType(): string {
    return this.translateService.instant('FLOORTYPES.' + this.configurationService.floorType);
  }
  get floorImage(): string {
    const floorType = this.configurationService.allowedFloorTypesRecord[this.configurationService.floorType];
    if (!floorType) {
      return '';
    }
    return floorType.content;
  }

  get plinthsImage(): string {
    // if (this.configurationService.plinthsInside && this.configurationService.plinthsOutside) {
    //   return '/assets/images/plinths_both.jpg';
    // }
    // if (this.configurationService.plinthsOutside) {
    //   return '/assets/images/plinths_outside.jpg';
    // }
    return '/assets/svg/icoon-plinten.svg';
  }
  get currentLocale() {
    return this.languageService.currentLanguage;
  }

  async addToCart() {
    if (this.addedToCart || this.hasCollisions || this.loading) {
      return;
    }
    if (environment.demo) {
      this.notificationService.showNotification('NOTIFICATIONS.DEMO');
      return;
    }
    await this.configurationService.saveAndGetConfigurationId();
    this.addedToCart = true;
    this.notificationService.showNotification('NOTIFICATIONS.ADDEDTOCART', NotificationService.DEFAULT_TIMEOUT);
    this.modularGoogleAnalyticsService.sendEvent('AddToChart', 'add', this.totalPrice);
  }

  getPricesForWallFinishes() {
    this.wallFinishPrices = this.priceService.calculateCostOfWallFinishesForAllFinishes(
      this.configurationService.configuration
    );
  }

  getImageForWallFinish(finishType: FinishTypes) {
    switch (finishType) {
      case FinishTypes.RVS304_LINE_BRUSH:
        return '/assets/images/metal_turned.jpg';
      case FinishTypes.RVS304_COLLED_ROLLED:
        return '/assets/images/metal.jpg';
      case FinishTypes.RVS304_DESIGN_5WL:
        return '/assets/images/5WL.jpg';
      case FinishTypes.RVS304_LINEN:
        return '/assets/images/linen.jpg';
      case FinishTypes.RVS304_ROUND_BRUSH:
      case FinishTypes.RVS316_ROUND_BRUSH:
        return '/assets/images/circular.jpg';
      case FinishTypes.STEEL:
        return '/assets/images/white9010.jpg';
      case FinishTypes.STEEL_PET55:
        return '/assets/images/white9010PET.jpg';
    }
  }
  getId(id: string) {
    if (!id) {
      return '';
    }
    const names = id.split('-');
    if (names.length > 1) {
      return +names[1] + 1;
    }
    return '';
  }

  innerWallPrice(innerWall: InnerWall) {
    return this.priceService.calculatePriceOfInnerWall(innerWall, this.configurationService.configuration);
  }
}
